/**
 * Main entry point for the application
 */
import { APIClient } from './api-client';
import { CodeEnhancer } from './code-enhancer';
import { MarkdownEditor } from './markdown-editor';
import { TOCGenerator } from './toc-generator';
import { ReleasesManager } from './releases';
import { StatusMonitor } from './status-monitor';
import { showLoading, showError, showSuccess, getElementById } from './utils';

/**
 * Quick helper to fetch and display API data
 */
async function fetchAPIData(url: string, targetElementId: string): Promise<void> {
  const element = getElementById(targetElementId);
  if (!element) return;

  showLoading(element);

  try {
    const api = new APIClient();
    const data = await api.get(url);

    // Display the data (customize based on your API structure)
    element.innerHTML = `
      <div class="card">
        <pre><code>${JSON.stringify(data, null, 2)}</code></pre>
      </div>
    `;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    showError(element, `Failed to fetch data: ${message}`);
  }
}

/**
 * Initialize sticky header with scroll effect
 */
function initStickyHeader(): void {
  const header = document.querySelector('header');
  if (!header) return;

  let lastScroll = 0;
  const scrollThreshold = 50;

  window.addEventListener('scroll', () => {
    const currentScroll = window.pageYOffset;

    if (currentScroll > scrollThreshold) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }

    lastScroll = currentScroll;
  });
}

/**
 * Initialize mobile menu
 */
function initMobileMenu(): void {
  const toggle = document.getElementById('mobile-menu-toggle');
  const nav = document.getElementById('main-nav');
  const overlay = document.getElementById('mobile-menu-overlay');

  if (!toggle || !nav || !overlay) return;

  const closeMenu = () => {
    toggle.classList.remove('active');
    nav.classList.remove('active');
    overlay.classList.remove('active');
    document.body.style.overflow = '';
  };

  const openMenu = () => {
    toggle.classList.add('active');
    nav.classList.add('active');
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  };

  toggle.addEventListener('click', () => {
    if (nav.classList.contains('active')) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  // Close menu when clicking overlay
  overlay.addEventListener('click', closeMenu);

  // Close menu when clicking a nav link
  nav.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', closeMenu);
  });

  // Close menu on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && nav.classList.contains('active')) {
      closeMenu();
    }
  });
}

/**
 * Initialize the application
 */
function init(): void {
  console.log('🚀 Korena Docs - Initialized with TypeScript');

  // Initialize code enhancer
  const codeEnhancer = new CodeEnhancer();
  codeEnhancer.init();

  // Initialize sticky header
  initStickyHeader();

  // Initialize mobile menu
  initMobileMenu();

  // Generate TOC if on docs page
  if (document.getElementById('toc-nav')) {
    const toc = new TOCGenerator();
    toc.generate();
  }

  // Create global API client
  const api = new APIClient();

  // Expose to window for inline scripts (backward compatibility)
  (window as any).api = api;
  (window as any).fetchAPIData = fetchAPIData;
  (window as any).showLoading = showLoading;
  (window as any).showError = showError;
  (window as any).showSuccess = showSuccess;
  (window as any).MarkdownEditor = MarkdownEditor;
  (window as any).TOCGenerator = TOCGenerator;
  (window as any).ReleasesManager = ReleasesManager;
  (window as any).StatusMonitor = StatusMonitor;
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for module usage
export { 
  APIClient, 
  CodeEnhancer, 
  MarkdownEditor, 
  TOCGenerator, 
  ReleasesManager,
  StatusMonitor,
  showLoading, 
  showError, 
  showSuccess, 
  fetchAPIData 
};

